import { useEffect, useState, useMemo} from "react";
import {
  Plus,
  Link2,
  Trash2,
  Save,
  Loader2,
  X,
  Search
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getJoinTables,
  getJoinColumns,
  createJoinInstruction,
  getJoinInstructionsBySource,
  deleteJoinInstruction,
} from "../../../../apiWithKnowledge";

/**
 * JoinInstructionsTab
 * --------------------------------------------------
 * - Visual join builder (Simple & Custom SQL)
 * - Multiple joins per source DB
 * - Databricks Genie–style guided UX
 */
export default function JoinInstructionsTab({ sourceDbId }) {
  const [tables, setTables] = useState([]);
  const [joins, setJoins] = useState([]);

  const [leftTable, setLeftTable] = useState("");
  const [rightTable, setRightTable] = useState("");
  const [leftColumns, setLeftColumns] = useState([]);
  const [rightColumns, setRightColumns] = useState([]);

  const [leftColumn, setLeftColumn] = useState("");
  const [rightColumn, setRightColumn] = useState("");

  const [relationshipType, setRelationshipType] = useState("Many-to-One");
  const [joinConditionType, setJoinConditionType] = useState("simple");
  const [customSql, setCustomSql] = useState("");
  const [instructionText, setInstructionText] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // <-- Added state

  /* --------------------------------------------------
     Memoized Search Filter
  -------------------------------------------------- */
  const filteredJoins = useMemo(() => {
    if (!searchQuery.trim()) return joins;
    
    const lower = searchQuery.toLowerCase();
    return joins.filter((j) => {
      const matchTables = `${j.left_table} ${j.right_table}`.toLowerCase().includes(lower);
      const matchColumns = `${j.left_column} ${j.right_column}`.toLowerCase().includes(lower);
      const matchSql = (j.custom_sql || "").toLowerCase().includes(lower);
      
      return matchTables || matchColumns || matchSql;
    });
  }, [joins, searchQuery]);
  /* --------------------------------------------------
     Load tables + joins
  -------------------------------------------------- */
  useEffect(() => {
    if (!sourceDbId) return;

    setLoading(true);
    Promise.all([
      getJoinTables(sourceDbId),
      getJoinInstructionsBySource(sourceDbId),
    ])
      .then(([tablesRes, joinsRes]) => {
        setTables(tablesRes.data.tables || []);
        setJoins(joinsRes.data || []);
      })
      .catch(() => toast.error("Failed to load join metadata"))
      .finally(() => setLoading(false));
  }, [sourceDbId]);

  /* --------------------------------------------------
     Load columns when table changes
  -------------------------------------------------- */
 useEffect(() => {
  if (!showCreateForm || !leftTable) return;

  getJoinColumns(sourceDbId, leftTable)
    .then((res) => {
      setLeftColumns(res.data.columns || []);
    })
    .catch(() => {
      toast.error("Failed loading left columns");
      setLeftColumns([]);
    });
}, [leftTable, sourceDbId, showCreateForm]);

  useEffect(() => {
  if (!showCreateForm || !rightTable) return;
      
      getJoinColumns(sourceDbId, rightTable)
      .then((res) =>{
        setRightColumns(res.data.columns || [])
  })
    .catch(()=>{
            toast.error("Failed loading Right columns");
            setRightColumns([]);
    });
  }, [rightTable, sourceDbId,showCreateForm]);

  /* --------------------------------------------------
     Save join
  -------------------------------------------------- */
  const handleSave = async () => {
    if (!leftTable || !rightTable) {
      toast.error("Please select both tables");
      return;
    }

    if (joinConditionType === "simple" && (!leftColumn || !rightColumn)) {
      toast.error("Please select join columns");
      return;
    }

    if (joinConditionType === "custom" && !customSql.trim()) {
      toast.error("Custom SQL is required");
      return;
    }

    setSaving(true);
    try {
      await createJoinInstruction({
        source_db_id: sourceDbId,
        left_table: leftTable,
        right_table: rightTable,
        relationship_type: relationshipType,
        join_condition_type: joinConditionType,
        left_column: joinConditionType === "simple" ? leftColumn : null,
        right_column: joinConditionType === "simple" ? rightColumn : null,
        custom_sql: joinConditionType === "custom" ? customSql : null,
        instruction_text: instructionText,
      });

      toast.success("Join instruction saved");

      // Reset form
      setLeftTable("");
      setRightTable("");
      setLeftColumn("");
      setRightColumn("");
      setCustomSql("");
      setInstructionText("");

      const refreshed = await getJoinInstructionsBySource(sourceDbId);
      setJoins(refreshed.data || []);
      setShowCreateForm(false);
    } catch (err) {
      toast.error("Failed to save join");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------
     Delete join
  -------------------------------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this join instruction?")) return;

    try {
      await deleteJoinInstruction(id);
      setJoins((prev) => prev.filter((j) => j.id !== id));
      toast.success("Join deleted");
    } catch {
      toast.error("Failed to delete join");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading joins…
      </div>
    );
  }
  const openCreateModal = () => {
  setLeftTable("");
  setRightTable("");
  setLeftColumn("");
  setRightColumn("");
  setLeftColumns([]);
  setRightColumns([]);
  setCustomSql("");
  setInstructionText("");
  setJoinConditionType("simple");
  setRelationshipType("Many-to-One");

  setShowCreateForm(true);
};

const closeCreateModal = () => {
  setShowCreateForm(false);

  // reset form state
  setLeftTable("");
  setRightTable("");
  setLeftColumn("");
  setRightColumn("");
  setLeftColumns([]);
  setRightColumns([]);
  setCustomSql("");
  setInstructionText("");
  setJoinConditionType("simple");
  setRelationshipType("Many-to-One");
};


  return (
    <div className="space-y-8 max-w-6xl">

      {/* ================= STICKY HEADER & SEARCH ================= */}
      <div className="sticky top-[-8px] z-20 bg-background py-3 mb-4  border-b border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        
        <h3 className="text-sm font-semibold flex items-center gap-2 shrink-0">
          <Link2 className="w-4 h-4" />
          Existing Joins
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-1">
            {filteredJoins.length}
          </span>
        </h3>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {/* Search Bar - Side-by-side */}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tables, columns, or SQL..."
              className="w-full border border-border rounded-md pl-9 pr-8 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Create Button */}
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors shrink-0 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border backdrop-blur-sm">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-border">
              <h2 className="text-xl font-semibold">
                Create Join Instruction
              </h2>
              <button
                onClick={closeCreateModal}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-5">
                {/* Tables */}
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={leftTable}
                    onChange={(e) => {
                      setLeftTable(e.target.value);
                      setLeftColumn("");       // RESET COLUMN
                      setLeftColumns([]);     //Reset list
                    }}
                    className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  >
                    <option value="">Left table</option>
                    {tables.map((t) => (
                     
                      <option
                       key={t}
                       value={t}
                       disabled={t === rightTable}
                      >
                      {t}
                      </option>
                    ))}
                  </select>

                  <select
                    value={rightTable}
                    onChange={(e) => {
                      setRightTable(e.target.value);
                      setRightColumn("");      // RESET COLUMN
                      setRightColumns([]); 
                    }}
                    className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  >
                    <option value="">Right table</option>
                    {tables.map((t) => (
                      <option key={t}
                       value={t}
                       disabled={t === leftTable}
                      >
                      {t}
                        </option>
                    ))}
                  </select>
                </div>

                {/* Join Type */}
                <div className="flex gap-4">
                  <select
                    value={relationshipType}
                    onChange={(e) => setRelationshipType(e.target.value)}
                    className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  >
                    <option>Many-to-One</option>
                    <option>One-to-Many</option>
                    <option>One-to-One</option>
                  </select>

                  <select
                    value={joinConditionType}
onChange={(e) => {
  const value = e.target.value;
  setJoinConditionType(value);

  if (value === "simple") {
    setCustomSql("");
  }

  if (value === "custom") {
    setLeftColumn("");
    setRightColumn("");
  }
}}
                    className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  >
                    <option value="simple">Simple Join</option>
                    <option value="custom">Custom SQL</option>
                  </select>
                </div>

                {/* Join Condition */}
                {joinConditionType === "simple" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      value={leftColumn}
                      onChange={(e) => setLeftColumn(e.target.value)}
                      className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                    >
                   <option value="">
                     {!leftTable
                       ? "Select left table first"
                        : leftColumns.length
                       ? "Left column"
                           : "Loading columns..."}
                             </option>

                      {leftColumns.map((c) => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>

                    <select
                      value={rightColumn}
                      onChange={(e) => setRightColumn(e.target.value)}
                      className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                    >
                      <option value="">
                     {!rightTable
                       ? "Select right table first"
                        : rightColumns.length
                       ? "Right column"
                           : "Loading columns..."}
                             </option>                   
                    {rightColumns.map((c) => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <textarea
                    rows={4}
                    value={customSql}
                    onChange={(e) => setCustomSql(e.target.value)}
                    placeholder="employees.dept_id = departments.id AND departments.active = 1"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono"
                  />
                )}

                {/* Instruction */}
                <textarea
                  rows={3}
                  value={instructionText}
                  onChange={(e) => setInstructionText(e.target.value)}
                  placeholder="Optional instruction to explain this join"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-border bg-muted/30">
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeCreateModal}
                  className="px-4 py-2 text-sm border border-border rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Join
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= EXISTING JOINS ================= */}
      <div className="space-y-3">
       
        {joins.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No join instructions defined yet.
          </p>
        )}

        {filteredJoins.map((j) => (
          <div
            key={j.id}
            className="border border-border rounded-lg p-4 flex justify-between items-start"
          >
            <div className="text-sm">
              <div className="font-medium">
                {j.left_table} → {j.right_table}
              </div>
              <div className="text-muted-foreground">
                {j.join_condition_type === "simple"
                  ? `${j.left_column} = ${j.right_column}`
                  : j.custom_sql}
              </div>
            </div>

            <button
              onClick={() => handleDelete(j.id)}
              className="text-destructive hover:bg-destructive/10 rounded p-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
